import createjs from 'createjs'

export default class CanvasHandler {
  constructor () {
    this.vm = null
    this.stage = null
    this.container = null
    if (window.devicePixelRatio) {
      this.deviceRatio = devicePixelRatio
    } else {
      this.deviceRatio = 1
    }
    this.basePositionWidth = 700 // 画像は横700x縦840
    this.basePositionHeight = 840 // 画像は横700x縦840
  }

  /**
  * 対象のVueインスタンスをセットする
  * @param { Object } vm Vueインスタンス
  */
  setVue (vm) { this.vm = vm }

  /**
  * カスタマイズで必要なフォントがあれば最初に読み込む
  * @param { Object } itemEmbData 刺繍関連のメニュー情報
  */
  loadFonts (itemEmbData) {
    if (typeof itemEmbData !== 'undefined') {
      const fontInfoArray = []
      itemEmbData.selects.forEach(element => {
        if (element.embroidery.fontFamily.match('https://firebasestorage.googleapis.com')) {
          fontInfoArray.push()
          const fontloader = new createjs.FontLoader({ src: [{
            src: 'url("' + element.embroidery.fontFamily + '")',
            family: element.embroidery.fontName
          }],
          type: 'font' })
          fontloader.load()
        } else {
          const fontloader = new createjs.FontLoader({
            src: element.embroidery.fontFamily,
            type: 'fontcss'
          })
          fontloader.load()
        }
      })
    }
  }

  /**
  * 刺繍を置いたときに刺繍の中心をCanvasの中心に合わせる
  * @param { Number } centerX Canvasの中心におきたいX座標
  */
  shiftHorizontalCenter (centerX) {
    this.resizeContainer()
    this.container.x += (this.basePositionWidth / 2 - centerX) * this.container.children[0].scaleX
  }

  /**
  * 選択中のカスタムパーツの合計金額を返す
  * @param { Array } customParts カスタムデータの選択パーツ配列
  * @return { Number } 合計金額
  */
  calcAmount (customParts) {
    let amount = 0
    customParts.forEach(element => {
      amount += element.price
    })
    return amount
  }

  /**
  * 選択中のカスタムパーツの中からselectIDが一致するものを返す
  * @param { Array } customParts カスタムデータの選択パーツ配列
  * @param { String } selectID 選択パーツのID
  * @return { Object } パーツのオブジェクト（無ければ「undefined」）
  */
  findSelectParts (customParts, selectID) {
    return customParts.find(element => element.selectID === selectID)
  }

  /**
  * クリック選択されたパーツのデータを作成（カスタムデータの入替用）
  * @param { Array } itemData カスタム中のアイテムデータが持つパーツ群
  * @param { Number } partsID 選択されたパーツのID
  * @param { String } selectID 選択されたパーツの中にある選択肢が持つID
  * @param { String } currentSceneLabel 現在表示中のシーンラベル
  * @param { Number } currentSceneNum 現在表示中のシーン番号
  * @return { Object }  クリック選択された選択肢のカスタムデータ入換用データ
  */
  createSelectPartsData (itemData, partsID, selectID, currentSceneLabel, currentSceneNum) {
    const partsData = itemData.find(element => element.id === partsID)
    const selectData = partsData.selects.find(element => element.selectID === selectID)
    const newSelectData = {}
    newSelectData['id'] = partsID
    newSelectData['type'] = partsData.type
    newSelectData['baseItem'] = partsData.baseItem
    newSelectData['label'] = partsData.label
    newSelectData['layer'] = partsData.layer
    newSelectData['ratio'] = partsData.ratio
    newSelectData['isEC'] = partsData.isEC
    newSelectData['selectID'] = selectData.selectID
    newSelectData['ggcd'] = selectData.ggcd
    newSelectData['gcd'] = selectData.gcd
    newSelectData['buildToOrder'] = selectData.buildToOrder
    newSelectData['deliveryDate'] = selectData.deliveryDate
    newSelectData['thumbnail'] = selectData.thumbnail
    newSelectData['name'] = selectData.name
    newSelectData['price'] = selectData.price
    newSelectData['stock'] = selectData.stock
    newSelectData['scene'] = []
    if (newSelectData.type === 'type1') {
      newSelectData['scene'] = selectData.scene
    } else if (newSelectData.type === 'type2') {
      const scene = {}
      scene.image = selectData.scene[0].image
      scene.label = currentSceneLabel
      scene.sceneNumber = currentSceneNum
      if (typeof partsData.initalPositions !== 'undefined') {
        const initalPosition = partsData.initalPositions.find(element => element.sceneNumber === currentSceneNum)
        scene.x = initalPosition.positionX
        scene.y = initalPosition.positionY
      } else {
        scene.x = this.basePositionWidth / 2
        scene.y = this.basePositionHeight / 2
      }
      scene.rotate = 0
      newSelectData['scene'].push(scene)
    } else if (newSelectData.type === 'type3') {
      const scene = {}
      scene.label = currentSceneLabel
      scene.sceneNumber = currentSceneNum
      if (selectData.embroidery.isLockPosition === true) {
        scene.x = selectData.embroidery.lockPositionX
        scene.y = selectData.embroidery.lockPositionY
      } else {
        scene.x = this.basePositionWidth / 2
        scene.y = this.basePositionHeight / 2
      }
      newSelectData['scene'].push(scene)

      const embroidery = {}
      embroidery.description = selectData.embroidery.description
      embroidery.fontColor = selectData.embroidery.fontColor
      embroidery.fontFamily = selectData.embroidery.fontFamily
      embroidery.fontName = selectData.embroidery.fontName
      embroidery.fontSize = selectData.embroidery.fontSize
      embroidery.inputRegulation = selectData.embroidery.inputRegulation
      embroidery.isLockPosition = selectData.embroidery.isLockPosition
      embroidery.maxInputs = selectData.embroidery.maxInputs
      newSelectData['embroidery'] = embroidery
    }
    return newSelectData
  }

  /**
  * Epoiのみで使用する特殊実装、引き手の型と表革のカラーを合わせるために、
  * 引き手のシーンに入る画像をカラー毎に変更する
  * @param { Array } itemData 全選択肢のパーツデータ
  * @param { Array } customData カスタム中のアイテムデータが持つパーツ群
  * @param { Number } partsID 選択されたパーツのID
  * @param { String } selectID 選択されたパーツの中にある選択肢が持つID
  * @param { String } menuLabel 選択されたパーツメニューのラベル
  * @return { Object }  クリック選択された選択肢のカスタムデータ入換用データ
  */
  createSelectPartsDataEpoi (itemData, customData, partsID, selectID, menuLabel) {
    const partsData = itemData.find(element => element.id === partsID)
    const selectData = partsData.selects.find(element => element.selectID === selectID)
    const newDataArray = []
    const newSelectData = {}
    newSelectData['id'] = partsID
    newSelectData['type'] = partsData.type
    newSelectData['baseItem'] = partsData.baseItem
    newSelectData['label'] = partsData.label
    newSelectData['layer'] = partsData.layer
    newSelectData['ratio'] = partsData.ratio
    newSelectData['selectID'] = selectData.selectID
    newSelectData['ggcd'] = selectData.ggcd
    newSelectData['gcd'] = selectData.gcd
    newSelectData['buildToOrder'] = selectData.buildToOrder
    newSelectData['deliveryDate'] = selectData.deliveryDate
    newSelectData['thumbnail'] = selectData.thumbnail
    newSelectData['name'] = selectData.name
    newSelectData['price'] = selectData.price
    newSelectData['stock'] = selectData.stock
    newSelectData['scene'] = []
    if (menuLabel === '表革') {
      newSelectData['scene'] = selectData.scene
      // 引き手データの中にあるシーン画像を表革カラーに合わせて変更
      const newHikiteData = customData.find(element => element.label === '引き手')
      const target = itemData.find(element => element.label === newHikiteData.name)
      const targetScene = target.selects.find(element => element.name === newSelectData.name).scene
      newHikiteData.scene = targetScene
      newDataArray.push(newHikiteData)
    }
    if (menuLabel === '引き手') {
      const target = itemData.find(element => element.label === newSelectData.name)
      const targetColor = customData.find(element => element.label === '表革').name
      const targetScene = target.selects.find(element => element.name === targetColor).scene
      newSelectData['scene'] = targetScene
    }
    newDataArray.push(newSelectData)
    return newDataArray
  }

  /**
  * 描画用のステージを作成
  * @param { String } canvas 描画するcanvasのid名
  */
  createStage (canvas) {
    this.stage = new createjs.Stage(canvas)
    createjs.Touch.enable(this.stage)
    createjs.Ticker.addEventListener('tick', this.stage)
    this.stage.addEventListener('mousedown', () => { this.clearOperationPanel() }, false)
    let stageTouchX, stageTouchY
    this.stage.addEventListener('mousedown', () => {
      if (this.vm.isScaleZoom) {
        stageTouchX = this.stage.x - this.stage.mouseX
        stageTouchY = this.stage.y - this.stage.mouseY
      }
    }, false)
    this.stage.addEventListener('pressmove', (e) => {
      if (this.vm.isScaleZoom && e.nativeEvent.touches.length === 1) {
        this.stage.x = this.stage.mouseX - Math.abs(stageTouchX)
        this.stage.y = this.stage.mouseY - Math.abs(stageTouchY)
      }
    }, false)
    this.stage.addEventListener('pressup', () => {
      if (this.vm.isScaleZoom) {
        const canvasWidth = this.stage.canvas.clientWidth
        const canvasHeight = this.stage.canvas.clientHeight
        if (Math.abs(this.stage.x) > canvasWidth) {
          if (this.stage.x < 0) {
            this.stage.x = 0 - canvasWidth
          } else {
            this.stage.x = 0 + canvasWidth
          }
        }
        if (Math.abs(this.stage.y) > canvasHeight / 2) {
          if (this.stage.y < 0) {
            this.stage.y = 0 - canvasHeight / 2
          } else {
            this.stage.y = 0 + canvasHeight / 2
          }
        }
      }
    }, false)
  }

  /**
  * キャンバスをスクリーンに合わせてサイズ調整
  * @param { Number } canvasWidth キャンバス幅
  * @param { Number } canvasHeight キャンバス高さ
  */
  resizeStage (canvasWidth, canvasHeight) {
    this.stage.canvas.width = canvasWidth
    this.stage.canvas.height = canvasHeight
    this.stage.canvas.width *= this.deviceRatio
    this.stage.canvas.height *= this.deviceRatio
    this.stage.canvas.style.width = String(this.stage.canvas.width / this.deviceRatio) + 'px'
    this.stage.canvas.style.height = String(this.stage.canvas.height / this.deviceRatio) + 'px'
    this.stage.scale = this.deviceRatio
    if (this.container !== null) this.resizeContainer()
  }

  /**
  * 描画エリアのメインコンテナを作成、stageに登録
  */
  createContainer () {
    this.container = new createjs.Container()
    this.stage.addChild(this.container)
    this.resizeContainer()
  }

  /**
  * 描画エリアのメインコンテナのサイズを調整
  */
  resizeContainer () {
    const containerAspect = 700 / 840 // 画像は横700x縦840
    const sidePadding = (this.stage.canvas.clientWidth < 700) ? 40 : 150 // 左右の余白
    const stageWidth = this.stage.canvas.clientWidth - (sidePadding * 2)
    const stageHeight = this.stage.canvas.clientHeight
    const stageAspect = stageWidth / stageHeight
    if (containerAspect >= stageAspect) { // コンテナが横長
      this.container.width = stageWidth
      this.container.height = stageWidth / containerAspect
      this.container.x = sidePadding
      this.container.y = (stageHeight - this.container.height) / 2
    } else { // コンテナが縦長
      this.container.width = stageHeight * containerAspect
      this.container.height = stageHeight
      this.container.x = (this.stage.canvas.clientWidth - this.container.width) / 2
      this.container.y = 0
    }
  }

  /**
  * 現在のstageにあるBaseItemのイメージ情報を取得
  * @param { Object } stage baseItemを探索するステージオブジェクト
  * @return { Object } stageに乗っているBaseItemのイメージ情報オブジェクト
  */
  getBaseBitmap (stage) {
    return stage.children.find(element => element.base === true)
  }

  /**
  * 描画中のイメージをキャンバス幅に合わせてサイズ調整
  */
  resizeBitmap () {
    const scale = this.container.width / this.getBaseBitmap(this.container).image.width
    this.container.children.forEach(element => {
      element.scaleX = scale * element.ratio
      element.scaleY = scale * element.ratio
      if (element.term === 'type2' || element.term === 'type3') {
        element.x = element.baseX * this.container.width / this.basePositionWidth
        element.y = element.baseY * this.container.height / this.basePositionHeight
      }
    })
    this.stage.x = 0
    this.stage.y = 0
    this.clearOperationPanel()
  }

  /**
  * 選択中のカスタムパーツをステージに描画
  * @param { Array } itemParts 全パーツ情報
  * @param { Array } customParts 選択中のカスタムパーツ
  * @param { Number } currentScene 現在のシーン番号（現在のシーンに応じて描画）
  */
  drawBitmapToStage (itemParts, customParts, currentScene) {
    this.clearOperationPanel()
    this.vm.changeStateSelectable(true)
    this.container.removeAllChildren()

    this.container.alpha = 0

    const manifest = []
    customParts.forEach(partsElement => {
      partsElement.scene.forEach(sceneElement => {
        if (sceneElement.sceneNumber === currentScene) {
          if (partsElement.type === 'type1' || partsElement.type === 'type2') {
            manifest.push({
              base: partsElement.baseItem,
              selectID: partsElement.selectID,
              term: partsElement.type,
              layer: partsElement.layer,
              ratio: partsElement.ratio,
              x: sceneElement.x,
              y: sceneElement.y,
              rotate: sceneElement.rotate,
              src: sceneElement.image
            })
          }
          if (partsElement.type === 'type3') {
            const inputText = partsElement.embroidery.inputText
            const fontStyle = partsElement.embroidery.fontSize + 'px ' + partsElement.embroidery.fontName
            const fontColor = partsElement.embroidery.fontColor
            const text = new createjs.Text(inputText, fontStyle, fontColor)
            text.base = partsElement.baseItem
            text.selectID = partsElement.selectID
            text.term = partsElement.type
            text.layer = partsElement.layer
            text.ratio = partsElement.ratio
            text.baseX = sceneElement.x
            text.baseY = sceneElement.y
            text.x = sceneElement.x * this.container.width / this.basePositionWidth
            text.y = sceneElement.y * this.container.height / this.basePositionHeight
            text.textAlign = 'center'
            text.textBaseline = 'middle'
            text.addEventListener('pressup', (puEvent) => { this.showOperationPanel(puEvent) }, false)
            this.container.addChild(text)
          }
        }
      })
    })

    const preload = new createjs.LoadQueue()
    preload.setMaxConnections(6)
    preload.loadManifest(manifest)

    preload.addEventListener('fileload', (flEvent) => {
      const bitmap = new createjs.Bitmap(flEvent.result)
      bitmap.base = flEvent.item.base
      bitmap.selectID = flEvent.item.selectID
      bitmap.term = flEvent.item.term
      bitmap.layer = flEvent.item.layer
      bitmap.ratio = flEvent.item.ratio
      this.container.addChild(bitmap)

      if (flEvent.item.term === 'type2') {
        bitmap.baseX = flEvent.item.x
        bitmap.baseY = flEvent.item.y
        bitmap.x = flEvent.item.x * this.container.width / this.basePositionWidth
        bitmap.y = flEvent.item.y * this.container.height / this.basePositionHeight
        bitmap.regX = bitmap.image.width / 2
        bitmap.regY = bitmap.image.height / 2
        bitmap.rotation = flEvent.item.rotate

        let dragPointX, dragPointY

        bitmap.addEventListener('mousedown', (mdEvent) => {
          this.clearOperationPanel()
          dragPointX = this.stage.mouseX / this.deviceRatio - bitmap.x
          dragPointY = this.stage.mouseY / this.deviceRatio - bitmap.y
          mdEvent.stopPropagation()
        }, false)

        bitmap.addEventListener('pressmove', (pmEvent) => {
          bitmap.x = this.stage.mouseX / this.deviceRatio - dragPointX
          bitmap.y = this.stage.mouseY / this.deviceRatio - dragPointY
          pmEvent.stopPropagation()
        }, false)

        bitmap.addEventListener('pressup', (puEvent) => { this.checkCanPut(puEvent) }, false)
      }
    }, false)

    preload.addEventListener('complete', (compEvent) => {
      let childIndex = 0
      for (let i = 0; childIndex < this.container.children.length - 1; i++) {
        const childArray = this.container.children.filter(element => element.layer === i)
        if (childArray.length > 0) {
          childArray.forEach(element => {
            this.container.setChildIndex(element, childIndex)
            childIndex++
          })
        }
      }
      itemParts.forEach(element => {
        if (typeof element.mask !== 'undefined') {
          const maskImage = element.mask.find(maskElement => maskElement.sceneNumber === currentScene).image
          const preload = new createjs.LoadQueue()
          preload.loadFile(maskImage)
          preload.addEventListener('fileload', (flEvent) => {
            const bitmap = new createjs.Bitmap(flEvent.result)
            bitmap.mask = true
            bitmap.ratio = 1
            const imageScale = this.container.width / this.getBaseBitmap(this.container).image.width
            bitmap.scaleX = imageScale * bitmap.ratio
            bitmap.scaleY = imageScale * bitmap.ratio
            this.container.addChildAt(bitmap, 0)
          }, false)
        }
      })
      const imageScale = this.container.width / this.getBaseBitmap(this.container).image.width
      this.container.children.forEach(element => {
        element.scaleX = imageScale * element.ratio
        element.scaleY = imageScale * element.ratio
      })
      this.container.alpha = 1
      this.vm.changeStateSelectable(false)
    }, false)
  }

  /**
  * 選択されたパーツのみをステージに描画
  * @param { Array } addParts 追加で選択されたパーツ
  * @param { Array } deleteParts 入れ替えるときに削除するパーツ
  * @param { Number } currentScene 現在のシーン番号（現在のシーンに応じて描画）
  */
  addBitmapToStage (addParts, deleteParts, currentScene) {
    this.clearOperationPanel()
    this.vm.changeStateSelectable(true)
    const manifest = []
    addParts.scene.forEach(sceneElement => {
      if (sceneElement.sceneNumber === currentScene) {
        if (addParts.type === 'type1' || addParts.type === 'type2') {
          manifest.push({
            base: addParts.baseItem,
            selectID: addParts.selectID,
            term: addParts.type,
            layer: addParts.layer,
            ratio: addParts.ratio,
            x: sceneElement.x,
            y: sceneElement.y,
            rotate: sceneElement.rotate,
            src: sceneElement.image
          })
        }
        if (addParts.type === 'type3') {
          this.deleteBitmapFromStage(addParts, currentScene)
          const inputText = addParts.embroidery.inputText
          const fontStyle = addParts.embroidery.fontSize + 'px ' + addParts.embroidery.fontName
          const fontColor = addParts.embroidery.fontColor
          const text = new createjs.Text(inputText, fontStyle, fontColor)
          text.base = addParts.baseItem
          text.selectID = addParts.selectID
          text.term = addParts.type
          text.layer = addParts.layer
          text.ratio = addParts.ratio
          text.baseX = sceneElement.x
          text.baseY = sceneElement.y
          text.x = sceneElement.x * this.container.width / this.basePositionWidth
          text.y = sceneElement.y * this.container.height / this.basePositionHeight
          text.textAlign = 'center'
          text.textBaseline = 'middle'
          text.addEventListener('pressup', (puEvent) => { this.showOperationPanel(puEvent) }, false)
          this.container.addChild(text)
          this.vm.changeStateSelectable(false)
        }
      }
    })

    if (manifest.length === 0) {
      this.vm.changeStateSelectable(false)
      return false
    }

    const preload = new createjs.LoadQueue()
    preload.loadManifest(manifest)

    preload.addEventListener('fileload', (flEvent) => {
      const bitmap = new createjs.Bitmap(flEvent.result)
      bitmap.base = flEvent.item.base
      bitmap.selectID = flEvent.item.selectID
      bitmap.term = flEvent.item.term
      bitmap.layer = flEvent.item.layer
      bitmap.ratio = flEvent.item.ratio
      this.container.addChild(bitmap)

      if (flEvent.item.term === 'type2') {
        bitmap.baseX = flEvent.item.x
        bitmap.baseY = flEvent.item.y
        bitmap.x = flEvent.item.x * this.container.width / this.basePositionWidth
        bitmap.y = flEvent.item.y * this.container.height / this.basePositionHeight
        bitmap.regX = bitmap.image.width / 2
        bitmap.regY = bitmap.image.height / 2
        bitmap.rotation = flEvent.item.rotate

        let dragPointX, dragPointY

        bitmap.addEventListener('mousedown', (mdEvent) => {
          this.clearOperationPanel()
          dragPointX = this.stage.mouseX / this.deviceRatio - bitmap.x
          dragPointY = this.stage.mouseY / this.deviceRatio - bitmap.y
          mdEvent.stopPropagation()
        }, false)

        bitmap.addEventListener('pressmove', (pmEvent) => {
          bitmap.x = this.stage.mouseX / this.deviceRatio - dragPointX
          bitmap.y = this.stage.mouseY / this.deviceRatio - dragPointY
          pmEvent.stopPropagation()
        }, false)

        bitmap.addEventListener('pressup', (puEvent) => { this.checkCanPut(puEvent) }, false)
      }

      let childIndex = 0
      for (let i = 0; childIndex < this.container.children.length - 1; i++) {
        const childArray = this.container.children.filter(element => element.layer === i)
        if (childArray.length > 0) {
          childArray.forEach(element => {
            this.container.setChildIndex(element, childIndex)
            childIndex++
          })
        }
      }

      const maskBitmap = this.container.children.find(element => element.mask === true)
      if (maskBitmap) this.container.setChildIndex(maskBitmap, 0)

      const imageScale = this.container.width / this.getBaseBitmap(this.container).image.width
      this.container.children.forEach(element => {
        element.scaleX = imageScale * element.ratio
        element.scaleY = imageScale * element.ratio
      })
      this.vm.changeStateSelectable(false)
      if (deleteParts !== null) this.deleteBitmapFromStage(deleteParts, currentScene)
    }, false)
  }

  deleteBitmapFromStage (deleteParts, currentScene) {
    let deleteTargetBitmap = null
    if (deleteParts.type === 'type1' || deleteParts.type === 'type3') {
      deleteTargetBitmap = this.container.children.find(child => {
        return child.selectID === deleteParts.selectID
      })
    }
    if (deleteParts.type === 'type2') {
      const currentSceneObj = deleteParts.scene.find(scene => {
        return scene.sceneNumber === currentScene
      })
      deleteTargetBitmap = this.container.children.find(child => {
        return child.selectID === deleteParts.selectID &&
        child.baseX === currentSceneObj.x &&
        child.baseY === currentSceneObj.y
      })
    }
    this.container.removeChild(deleteTargetBitmap)
  }

  /**
  * 移動式アイテム（ワッペン等）を置いたとき、貼付可能エリアか判定、貼付不可エリアであれば、
  * 元の位置に戻してエラーメッセージを表示、貼付可能エリアであればカスタムデータの座標を更新
  * @param { Object } puEvent pressupイベントの対象オブジェクト
  */
  checkCanPut (puEvent) {
    const isOutItems = []
    const isHitItems = []

    const targetHalfWidth = puEvent.target.image.width * puEvent.target.ratio / 2
    const targetHalfHeight = puEvent.target.image.height * puEvent.target.ratio / 2

    const maskImage = this.container.children.find(element => element.mask === true)

    this.container.children.forEach(element => {
      if (puEvent.target.id !== element.id) {
        const point = puEvent.target.localToLocal(puEvent.target.regX, puEvent.target.regY, element)
        const isHitCenter = element.hitTest(point.x, point.y)
        const isHitCenterLeft = element.hitTest(point.x - targetHalfWidth, point.y)
        const isHitCenterRight = element.hitTest(point.x + targetHalfWidth, point.y)
        const isHitTopCenter = element.hitTest(point.x, point.y - targetHalfHeight)
        const isHitBottomCenter = element.hitTest(point.x, point.y + targetHalfHeight)
        const isHitTopLeft = element.hitTest(point.x - targetHalfWidth, point.y - targetHalfHeight)
        const isHitTopRight = element.hitTest(point.x + targetHalfWidth, point.y - targetHalfHeight)
        const isHitBottomLeft = element.hitTest(point.x - targetHalfWidth, point.y + targetHalfHeight)
        const isHitBottomRight = element.hitTest(point.x + targetHalfWidth, point.y + targetHalfHeight)

        if (!maskImage) {
          if (element.term === 'type1') { // 当たり判定画像がない場合
            if (isHitTopLeft && isHitTopRight && isHitBottomLeft && isHitBottomRight &&
              isHitCenter && isHitCenterLeft && isHitCenterRight && isHitTopCenter && isHitBottomCenter) {
              isOutItems.push(false)
            } else {
              isOutItems.push(true)
            }
          }
        }
        if (element.term === 'type2' || element.term === 'type3') {
          if (isHitTopLeft || isHitTopRight || isHitBottomLeft || isHitBottomRight ||
            isHitCenter || isHitCenterLeft || isHitCenterRight || isHitTopCenter || isHitBottomCenter) {
            isHitItems.push(true)
          } else {
            isHitItems.push(false)
          }
        }
      }
    })
    if (maskImage) { // 当たり判定画像がある場合
      const point = puEvent.target.localToLocal(puEvent.target.regX, puEvent.target.regY, maskImage)
      const isHitCenter = maskImage.hitTest(point.x, point.y)
      const isHitCenterLeft = maskImage.hitTest(point.x - targetHalfWidth, point.y)
      const isHitCenterRight = maskImage.hitTest(point.x + targetHalfWidth, point.y)
      const isHitTopCenter = maskImage.hitTest(point.x, point.y - targetHalfHeight)
      const isHitBottomCenter = maskImage.hitTest(point.x, point.y + targetHalfHeight)
      const isHitTopLeft = maskImage.hitTest(point.x - targetHalfWidth, point.y - targetHalfHeight)
      const isHitTopRight = maskImage.hitTest(point.x + targetHalfWidth, point.y - targetHalfHeight)
      const isHitBottomLeft = maskImage.hitTest(point.x - targetHalfWidth, point.y + targetHalfHeight)
      const isHitBottomRight = maskImage.hitTest(point.x + targetHalfWidth, point.y + targetHalfHeight)
      if (isHitTopLeft && isHitTopRight && isHitBottomLeft && isHitBottomRight &&
        isHitCenter && isHitCenterLeft && isHitCenterRight && isHitTopCenter && isHitBottomCenter) {
        isOutItems.push(false)
      } else {
        isOutItems.push(true)
      }
    }
    if (isHitItems.indexOf(true) === -1) {
      if (isOutItems.indexOf(false) === -1) {
        puEvent.target.x = puEvent.target.baseX * this.container.width / this.basePositionWidth
        puEvent.target.y = puEvent.target.baseY * this.container.height / this.basePositionHeight
        this.vm.setInfoMessage('貼付可能エリア外です')
      } else {
        const selectID = puEvent.target.selectID
        const beforeX = puEvent.target.baseX
        const beforeY = puEvent.target.baseY
        const x = puEvent.target.x * this.basePositionWidth / this.container.width
        const y = puEvent.target.y * this.basePositionHeight / this.container.height
        this.vm.updateLocation(selectID, beforeX, beforeY, x, y)
        puEvent.target.baseX = x
        puEvent.target.baseY = y
        this.showOperationPanel(puEvent)
      }
    } else {
      puEvent.target.x = puEvent.target.baseX * this.container.width / this.basePositionWidth
      puEvent.target.y = puEvent.target.baseY * this.container.height / this.basePositionHeight
      this.vm.setInfoMessage('アイテムを重ねることはできません')
    }
  }

  /**
  * 対象オブジェクトからタッチを離した時に操作パネル（ゴミ箱&回転）を表示
  * @param { Object } e パネルを表示させる対象のイベントオブジェクト
  */
  showOperationPanel (e) {
    const panelContainer = new createjs.Container()
    panelContainer.name = 'panelContainer'

    const panelRect = new createjs.Graphics()
    let rectWidth, rectHeight
    if (e.target.term === 'type3') {
      rectWidth = e.target.getMeasuredWidth()
      rectHeight = e.target.getMeasuredHeight()
    } else {
      rectWidth = e.target.image.width
      rectHeight = e.target.image.height
    }
    panelRect.beginStroke('#70AFFF')
    panelRect.setStrokeStyle(3)
    panelRect.rect(0, 0, rectWidth, rectHeight)

    const panelShape = new createjs.Shape(panelRect)
    panelShape.scale = e.target.scale
    panelShape.x = e.target.x
    panelShape.y = e.target.y

    const deleteBtn = new createjs.Bitmap(require('@/assets/images/canvas/canvas-delete.svg'))
    const rotateBtn = new createjs.Bitmap(require('@/assets/images/canvas/canvas-rotate.svg'))
    deleteBtn.image.width = deleteBtn.image.height = 33
    rotateBtn.image.width = rotateBtn.image.height = 33
    const btnScale = (this.stage.canvas.clientWidth < 700) ? 1.3 : 2.5
    deleteBtn.scale = rotateBtn.scale = btnScale
    deleteBtn.regX = deleteBtn.image.width / 2
    deleteBtn.regY = deleteBtn.image.height / 2
    rotateBtn.regX = rotateBtn.image.width / 2
    rotateBtn.regY = rotateBtn.image.height / 2
    deleteBtn.y = panelShape.y + rectHeight * panelShape.scale / 2 + 5
    rotateBtn.y = panelShape.y + rectHeight * panelShape.scale / 2 + 5
    deleteBtn.x = panelShape.x - deleteBtn.image.width * deleteBtn.scale / 2 - 5
    rotateBtn.x = panelShape.x + rectWidth * panelShape.scale + rotateBtn.image.width * rotateBtn.scale / 2 + 5

    panelContainer.regX = rectWidth * e.target.scale / 2
    panelContainer.regY = rectHeight * e.target.scale / 2

    deleteBtn.addEventListener('pressup', () => {
      this.vm.deleteParts(e.target.selectID, e.target.baseX, e.target.baseY)
    }, false)
    rotateBtn.addEventListener('mousedown', (mdEvent) => { mdEvent.stopPropagation() }, false)
    rotateBtn.addEventListener('pressmove', (pmEvent) => {
      const paddingX = (this.stage.canvas.clientWidth - this.container.width) / 2 // コンテナの座標から計算
      const paddingY = (this.stage.canvas.clientHeight - this.container.height) / 2 // コンテナの座標から計算
      const dx = this.stage.mouseX / this.deviceRatio - paddingX - e.target.x
      const dy = this.stage.mouseY / this.deviceRatio - paddingY - e.target.y
      const radians = Math.atan2(dy, dx)
      const degrees = radians * 180 / Math.PI
      e.target.rotation = degrees
      pmEvent.stopPropagation()
    }, false)
    rotateBtn.addEventListener('pressup', () => {
      this.vm.updateRotation(e.target.selectID, e.target.baseX, e.target.baseY, e.target.rotation)
    }, false)

    panelContainer.addChild(deleteBtn)
    panelContainer.addChild(rotateBtn)
    if (e.target.term === 'type3') panelContainer.removeChild(rotateBtn)
    panelContainer.addChild(panelShape)
    this.container.addChild(panelContainer)
  }

  /**
  * 操作パネル（ゴミ箱&回転）を削除
  */
  clearOperationPanel () {
    const panelContainer = this.container.getChildByName('panelContainer')
    this.container.removeChild(panelContainer)
  }

  /**
  * カスタムデータ保存前に選択されたアイテムが在庫切れでないかをチェックする
  * @param { Array } customParts カスタムデータの選択パーツ配列
  */
  checkSoldOut (customParts) {
    let checkStock = true
    customParts.forEach(element => {
      if (element.stock === 0) {
        checkStock = false
        return false
      }
    })
    return checkStock
  }

  /**
  * 現在の選択パーツから保存用のイメージデータ（base64）配列をPromise形式で返却
  * @param { String } canvas 描画するcanvasのid名
  * @param { Array } customParts カスタムデータの選択パーツ配列
  * @param { Number } totalScene カスタム中のシーン数
  * @return { Array } base64画像データ配列（シーンラベル付）
  */
  createSaveImages (canvas, customParts, totalScene) {
    return new Promise((resolve, reject) => {
      const cacheStageWidth = 700 // 画像は横700x縦840
      const cacheStageHeight = 840 // 画像は横700x縦840
      const customImageArray = []
      let countScene = 0
      const createCustomImageArray = (canvas, customParts, totalScene) => {
        const cacheStage = new createjs.Stage(canvas)
        cacheStage.cache(0, 0, cacheStageWidth, cacheStageHeight)
        const bitmapManifest = []
        customParts.forEach(partsElement => {
          partsElement.scene.forEach(sceneElement => {
            if (sceneElement.sceneNumber === countScene) {
              if (partsElement.type === 'type1' || partsElement.type === 'type2') {
                bitmapManifest.push({
                  base: partsElement.baseItem,
                  term: partsElement.type,
                  selectID: partsElement.selectID,
                  layer: partsElement.layer,
                  ratio: partsElement.ratio,
                  src: sceneElement.image,
                  x: sceneElement.x,
                  y: sceneElement.y,
                  rotate: sceneElement.rotate,
                  label: sceneElement.label
                })
              }
              if (partsElement.type === 'type3') {
                const inputText = partsElement.embroidery.inputText
                const fontStyle = partsElement.embroidery.fontSize + 'px ' + partsElement.embroidery.fontName
                const fontColor = partsElement.embroidery.fontColor
                const text = new createjs.Text(inputText, fontStyle, fontColor)
                text.base = partsElement.baseItem
                text.selectID = partsElement.selectID
                text.term = partsElement.type
                text.layer = partsElement.layer
                text.ratio = partsElement.ratio
                text.baseX = sceneElement.x
                text.baseY = sceneElement.y
                text.x = sceneElement.x * cacheStageWidth / this.basePositionWidth
                text.y = sceneElement.y * cacheStageHeight / this.basePositionHeight
                text.textAlign = 'center'
                text.textBaseline = 'middle'
                cacheStage.addChild(text)
              }
            }
          })
        })
        const preload = new createjs.LoadQueue()
        preload.setMaxConnections(6)
        preload.loadManifest(bitmapManifest)
        preload.addEventListener('fileload', (e) => {
          const bitmap = new createjs.Bitmap(e.result)
          bitmap.base = e.item.base
          bitmap.term = e.item.term
          bitmap.label = e.item.label
          bitmap.layer = e.item.layer
          bitmap.ratio = e.item.ratio
          cacheStage.addChild(bitmap)
          if (e.item.term === 'type2') {
            bitmap.x = e.item.x * cacheStageWidth / this.basePositionWidth
            bitmap.y = e.item.y * cacheStageHeight / this.basePositionHeight
            bitmap.regX = bitmap.image.width / 2
            bitmap.regY = bitmap.image.height / 2
            bitmap.rotation = e.item.rotate
          }
        }, false)
        preload.addEventListener('complete', (e) => {
          let childIndex = 0
          for (let i = 0; childIndex < cacheStage.children.length - 1; i++) {
            const childArray = cacheStage.children.filter(element => element.layer === i)
            if (childArray.length > 0) {
              childArray.forEach(element => {
                cacheStage.setChildIndex(element, childIndex)
                childIndex++
              })
            }
          }
          cacheStage.label = this.getBaseBitmap(cacheStage).label
          const scale = cacheStageWidth / this.getBaseBitmap(cacheStage).image.width
          cacheStage.children.forEach(element => {
            element.scaleX = scale * element.ratio
            element.scaleY = scale * element.ratio
          })
          cacheStage.updateCache()
          const customImage = {}
          customImage.src = cacheStage.cacheCanvas.toDataURL()
          customImage.label = cacheStage.label
          customImageArray.push(customImage)
          countScene++
          createCustomImageArray(canvas, customParts, totalScene)
          if (countScene >= totalScene) {
            resolve(customImageArray)
          }
        }, false)
      }
      createCustomImageArray(canvas, customParts, totalScene)
    })
  }
}



// WEBPACK FOOTER //
// ./src/assets/class/CanvasHandler.js